import Content from "@arelia-components/layout/Content";
import Header from "@arelia-components/layout/Header";
import { ContentBox } from "@arelia-components/ui-atoms/ContentBox";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PATHS } from "src/routes";
import { FANS_NAV_OPTS } from "../constant";
import SettingsPresenter from "./SettingsPresenter";
import SettingsProcessSteps from "./SettingsProcessSteps";
import { useAttachTwilioNumber, useBrandDataAPI, useBrandDataUpdateAPI, useBrandRegistrationStatusAPI, useTwilioAvailableNumbersAPI } from "./settings.api";
import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";
import { BrandData, TwilioRegStatus } from "./models";
import { Alert, AlertTitle, Box } from "@mui/material";
import RegistrationStatus from "./RegistrationStatus";
import { GridLayout } from "@arelia-components/layout/GridLayout";
import { useQueryClient } from "react-query";
import { NOTIFICATION_QUERY_KEY } from "src/api/notifications";

export default function SettingsContainer() {
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [refetchRegStatusActive, setRefetchRegStatusActive] = useState(true);
  const [refetchInterval, setRefetchInterval] = useState(3000);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const refetchRegStatusTimer = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (pathname === PATHS.FANS) {
      navigate(PATHS.FANS_DASHBOARD);
    }
  }, [pathname]);
  const navOptions = FANS_NAV_OPTS.map((opt) => ({
    ...opt,
    // disabled: account?.plan === 'Starter',
    disabled: false
  }));

  const {
    data: brandData,
    isLoading,
    error,
  } = useBrandDataAPI();

  const {
    data: registrationStatus,
    isLoading: isRegStatusLoading,
    refetch: refetchRegStatus,
  } = useBrandRegistrationStatusAPI(refetchRegStatusActive, refetchRegStatusActive ? refetchInterval : false);

  const attachNumberMutation = useAttachTwilioNumber(() => refetchRegStatus());

  useEffect(() => {
    var shouldEditModeBeSet = !!registrationStatus
      && !registrationStatus.inProgress
      && (
        !registrationStatus?.started
        || (registrationStatus.failed && !registrationStatus.brandCreated));
    if (shouldEditModeBeSet) {
      setEditMode(true);
    }
    if (shouldEditModeBeSet || registrationStatus?.completed || (registrationStatus?.failed && registrationStatus.brandCreated)) {
      setRefetchRegStatusActive(false);
    }
  }, [registrationStatus?.started, registrationStatus?.failed, registrationStatus?.completed, registrationStatus?.inProgress]);

  useEffect(() => {
    if (refetchInterval !== 2000) {
      setRefetchInterval(2000);
    }
  }, [registrationStatus?.nextStepCheckTimer]);

  useEffect(() => {
  const currentStep = registrationStatus?.stepGroups
    .flatMap(group => group.steps)
    .find(step => step.status === "InProgress");

  if (currentStep?.step.startsWith("3.2")) {
    queryClient.invalidateQueries(NOTIFICATION_QUERY_KEY);
  }
}, [registrationStatus, queryClient]);

  const updateBrandMutation = useBrandDataUpdateAPI(() => {
    setRefetchRegStatusActive(true);
  });

  const onEdit = (data: BrandData) => {
    updateBrandMutation.mutate(data);
    setEditMode(false);
    setRefetchInterval(1000);
    setRefetchRegStatusActive(true);
  };

  const onSelectNumber = (phoneNumber: string) => {
    attachNumberMutation.mutate({ phoneNumber: phoneNumber.replace('+', '') });
  };

  const failedTestPostfix = registrationStatus?.brandCreated ? 'Please contact support.' : 'Please check the form and resubmit.';
  const alert = registrationStatus?.failed
    ? <Alert severity="error" variant="filled">Registration failed: {registrationStatus.lastStepErr}. {failedTestPostfix}</Alert> : null;

  return (
    <ContentBox>
      <Header
        title="Settings"
        navOptions={navOptions}
        navValue={pathname}
        setNavValue={navigate}
      />
      <SpinnerWrapper loading={isLoading}>
        <SettingsProcessSteps hideIfCompleted={false} />
        <Alert severity="info" variant="filled">
          <AlertTitle>Learn about the A2P 10DLC Registration</AlertTitle>
          <b>Anyone sending SMS/MMS messages over a 10DLC number from an application to the US must register for A2P 10DLC.</b>
          <p>
            A2P (Application to Person) 10DLC (10 digit long code) is the standard that United States telecom carriers have put in place to ensure that SMS traffic to US end-users through long code phone numbers is verified and consensual.
            Registering for A2P 10DLC results in lower message filtering and higher messaging throughput.
            Read more about <a target="_blank" href="https://www.campaignregistry.com/wp-content/uploads/TCR-Intro_V4-2.pdf">The Campain Registry (TCR)</a>
          </p>
        </Alert>
        {alert}
        <GridLayout customColumns={registrationStatus?.started ? '65% 35%' : '100%'} customRows={1}>
          {brandData && <SettingsPresenter data={brandData} onEdit={onEdit} editing={editMode} />}
          {registrationStatus?.started && <RegistrationStatus
            onAttachNumber={onSelectNumber}
            statusData={registrationStatus}
            refresh={refetchRegStatus}
            loading={isRegStatusLoading}
          />}
        </GridLayout>

      </SpinnerWrapper>
      {/* <ComingSoonScreen /> */}
    </ContentBox>
  );
}

const styles = {
  alert: {},
  dataContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: "20px",
  }
};
